import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const trdRelated: RL[] = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description:
      "The 5-day, fMRI-guided protocol for treatment-resistant depression",
  },
  {
    title: "Am I a Candidate? Free Screener",
    href: "/am-i-a-candidate-screener",
    description: "No email required — see if SAINT® may be right for you",
  },
  {
    title: "Check My Insurance",
    href: "/check-insurance-options",
    description: "Free benefits verification for any carrier",
  },
  {
    title: "SAINT® vs fMRI-Guided Accelerated TMS",
    href: "/learn/saint-vs-accelerated-tms",
    description:
      "What's the same, what isn't, and what the FDA clearance covers",
  },
  {
    title: "SAINT® TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "Price, insurance, Medicare, and financing",
  },
  {
    title: "SAINT® TMS Locations",
    href: "/learn/saint-tms-locations",
    description: "Verified providers by state and travel options",
  },
  {
    title: "What Is Accelerated TMS?",
    href: "/what-is-accelerated-tms",
    description: "Complete guide to accelerated TMS protocols",
  },
  {
    title: "SAINT® vs One-Day TMS (ONE-D)",
    href: "/learn/saint-vs-one-day-tms",
    description: "Comparing 5-day and 1-day protocols",
  },
  {
    title: "SAINT® vs Deep TMS (BrainsWay)",
    href: "/learn/saint-vs-deep-tms",
    description: "H-coil vs personalized targeting, side by side",
  },
  {
    title: "SAINT® vs MeRT",
    href: "/learn/saint-vs-mert",
    description: "Evidence, FDA status, time, and cost compared",
  },
  {
    title: "SAINT® vs Psilocybin Therapy",
    href: "/learn/saint-vs-psilocybin",
    description: "FDA status, evidence, and legal access compared",
  },
  {
    title: "SAINT® vs VNS",
    href: "/learn/saint-vs-vns",
    description: "Non-invasive 5-day vs surgically implanted device",
  },
  {
    title: "SAINT® vs Ketamine",
    href: "/learn/saint-vs-ketamine",
    description: "Durability, cost, and combining both in one clinic",
  },
  {
    title: "SAINT® vs ECT",
    href: "/learn/saint-vs-ect",
    description: "Non-invasive vs anesthesia-based: outcomes and trade-offs",
  },
  {
    title: "SAINT® vs SPRAVATO®",
    href: "/learn/saint-vs-spravato",
    description: "5-day TMS vs esketamine nasal spray compared",
  },
  {
    title: "Is SAINT® TMS Safe?",
    href: "/learn/is-saint-tms-safe",
    description: "Side effects, long-term effects, and risks explained",
  },
  {
    title: "Can SAINT® Make Depression Worse?",
    href: "/learn/can-saint-make-depression-worse",
    description: "The 'TMS dip' and what to expect during treatment",
  },
  {
    title: "What If SAINT® Doesn't Work?",
    href: "/learn/what-if-saint-doesnt-work",
    description: "Next steps and options under one roof",
  },
  {
    title: "How Long Do SAINT® Results Last?",
    href: "/learn/what-happens-after-saint-tms",
    description: "Durability, follow-up data, and maintenance",
  },
  {
    title: "SAINT® TMS Success Rates",
    href: "/learn/saint-tms-success-rates",
    description: "Remission and response rates from the trials",
  },
  {
    title: "Alternatives to Antidepressants",
    href: "/learn/alternative-to-antidepressants",
    description: "Non-medication options for treatment-resistant depression",
  },
  {
    title: "Why Isn't My Antidepressant Working?",
    href: "/learn/why-isnt-my-antidepressant-working",
    description: "Causes of inadequate response and what to do",
  },
  {
    title: "Fast Depression Treatment",
    href: "/learn/fast-depression-treatment",
    description: "Rapid-acting options for treatment-resistant depression",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "What counts as treatment-resistant depression?",
    answer:
      "Treatment-resistant depression is major depressive disorder that has not improved adequately after at least two antidepressant medication trials of adequate dose and duration — typically 6 to 8 weeks at a therapeutic dose — in the current episode. This is the definition used in the FDA's 2018 draft guidance and in esketamine clinical trials. It describes treatment history, not a separate diagnosis, and it does not mean the depression cannot be treated.",
  },
  {
    question: "What is the most effective treatment for TRD?",
    answer:
      "No single treatment is most effective for every person. ECT has the highest remission rates for severe or psychotic depression but carries anesthesia and cognitive risks. SAINT® showed 79% remission in the 2022 randomized trial (Cole et al., 2022). Individual results may vary. Standard TMS, esketamine, medication augmentation, and psychotherapy all have evidence for specific situations. The right choice depends on your history, preferences, and medical profile, decided with a psychiatrist.",
  },
  {
    question: "What's the fastest option for treatment-resistant depression?",
    answer:
      "SAINT® delivers 10 sessions per day over 5 days, making it the fastest FDA-cleared brain stimulation option. SPRAVATO® (esketamine) can produce mood changes within hours of a session but requires ongoing maintenance. IV ketamine may act within hours to days but is used off-label for depression. ECT typically requires 6 to 12 sessions over 2 to 4 weeks. Speed of onset is one factor; durability, cost, and side-effect profile matter too.",
  },
  {
    question: "Does insurance cover TRD treatments?",
    answer:
      "Coverage varies by plan and by treatment. Standard TMS is widely covered for MDD that has not improved on prior antidepressant medication. SAINT® may be covered; coverage varies by plan and we verify your benefits for free. SPRAVATO® is covered by many commercial and Medicare plans. ECT is generally covered. IV ketamine is typically self-pay. We verify your benefits for any carrier at no cost before treatment begins.",
  },
  {
    question: "Can I combine treatments for treatment-resistant depression?",
    answer:
      "Yes. Many people benefit from combining approaches — for example, TMS or SAINT® alongside medication management and psychotherapy. NWCOA offers SAINT®, standard TMS, SPRAVATO®, ketamine, medication management, and psychotherapy in the same clinic, so your psychiatrist can coordinate a combined plan rather than sending you to separate providers. The right combination depends on your history and should be decided with your treatment team.",
  },
  {
    question: "How do I know which TRD treatment is right for me?",
    answer:
      "Start with a full re-evaluation by a psychiatrist. This should include reviewing whether past medication trials were truly adequate, screening for bipolar disorder and comorbid conditions, and a medical workup including thyroid function. Then discuss the full range of options in the context of your history, preferences, insurance, and time constraints. A free consultation is the first step. You can also take a free screener at [[/am-i-a-candidate-screener]] to see if SAINT® may be appropriate.",
  },
];

export const page6 = makeArticle({
  slug: "what-is-treatment-resistant-depression",
  title: "Treatment-Resistant Depression: Every Option Compared (2026 Guide)",
  description:
    "A plain-language 2026 guide to treatment-resistant depression: definition, every evidence-based option compared, costs, and how to choose.",
  canonical: "/learn/what-is-treatment-resistant-depression",
  h1: "Treatment-Resistant Depression: The Complete 2026 Guide",
  breadcrumbLabel: "Treatment-Resistant Depression",
  shortAnswer:
    "Treatment-resistant depression (TRD) is major depressive disorder that has not improved adequately after at least two antidepressant trials of adequate dose and duration. It describes treatment history, not a separate diagnosis — and it does not mean the depression cannot be treated. Options include SAINT® accelerated TMS (FDA-cleared, 5 days), standard TMS, deep TMS, SPRAVATO® (esketamine), ketamine, ECT, VNS, medication strategies, and psychotherapy. SAINT® at NWCOA starts at $25,000; coverage varies by plan and we verify your benefits for free. If you are in crisis, call or text 988.",
  keyTakeaways: [
    "TRD = MDD that has not improved after at least 2 adequate antidepressant trials (adequate dose, 6–8 weeks).",
    "In the STAR*D study, remission rates fell from ~37% after the first medication to ~13% after the fourth.",
    "TRD describes treatment history, not a separate diagnosis — it does not mean depression cannot be treated.",
    "SAINT® showed 79% remission in the 2022 randomized trial (Cole et al., 2022). Individual results may vary.",
    "Every option — SAINT®, standard TMS, deep TMS, ECT, VNS, SPRAVATO®, ketamine, medication, psychotherapy — has different evidence, time commitment, cost, and side-effect profile.",
    "SAINT® at NWCOA starts at $25,000; coverage varies by plan; we verify your benefits for free.",
    "If you are in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "Quick Comparison: Every TRD Option at a Glance",
      table: {
        headers: [
          "Option",
          "FDA status",
          "Time commitment",
          "Typical response / remission",
          "Durability",
          "Cost / coverage",
        ],
        rows: [
          [
            "SAINT® (accelerated TMS)",
            "FDA-cleared (K220177, Sept 2022)",
            "5 days, 10 sessions/day",
            "79% remission (Cole et al., 2022)",
            "Follow-up data emerging; some relapse possible",
            "Starting at $25,000; coverage varies by plan; free benefits verification",
          ],
          [
            "Standard TMS (rTMS)",
            "FDA-cleared",
            "~6 weeks, 5 sessions/week",
            "~30–40% remission (various studies)",
            "Maintenance sessions may help sustain response",
            "May be covered; coverage varies by plan",
          ],
          [
            "Deep TMS (BrainsWay H-coil)",
            "FDA-cleared",
            "~6 weeks, 5 sessions/week",
            "~30–35% remission (various studies)",
            "Similar to standard TMS; maintenance may help",
            "May be covered; coverage varies by plan",
          ],
          [
            "ECT",
            "Established psychiatric treatment",
            "2–3×/week for 6–12 sessions",
            "~50–70% remission for severe depression",
            "High initial response; relapse risk without maintenance",
            "May be covered; coverage varies by plan",
          ],
          [
            "VNS (vagus nerve stimulation)",
            "FDA-approved (adjunct for chronic/recurrent depression)",
            "Surgical implant + ongoing office visits",
            "Gradual improvement over months to years",
            "Long-term; device remains implanted",
            "May be covered; coverage varies by plan; surgery required",
          ],
          [
            "SPRAVATO® (esketamine nasal spray)",
            "FDA-approved (2019; monotherapy Jan 2025)",
            "2×/week × 4 weeks, then taper to maintenance",
            "~28% remission in pivotal trials",
            "Ongoing maintenance sessions required",
            "May be covered; coverage varies by plan; REMS program",
          ],
          [
            "IV ketamine",
            "Off-label for depression",
            "6 infusions over 2–3 weeks",
            "~50–70% response in open-label studies",
            "Short-lived; maintenance infusions often needed",
            "Typically self-pay; coverage varies",
          ],
          [
            "Medication optimization / augmentation",
            "FDA-approved drugs",
            "Ongoing; 6–8 weeks per trial",
            "~18–28% remission per STAR*D levels 1–2",
            "Ongoing; relapse if discontinued",
            "May be covered; coverage varies by plan",
          ],
          [
            "Psychotherapy (CBT, IPT, BA)",
            "Established treatment",
            "Weekly, 12–20 sessions",
            "~30–40% response in TRD populations",
            "Skills-based; can be durable with practice",
            "May be covered; coverage varies by plan",
          ],
        ],
      },
      paragraphsAfter: [
        "This table summarizes the major options. Each is explained in detail below, with the evidence, time commitment, side-effect profile, and cost considerations that help you and your psychiatrist decide what fits your situation. No treatment guarantees remission — trial statistics describe groups, not individuals. Individual results may vary.",
      ],
    },
    {
      heading: "What Treatment-Resistant Depression Is",
      paragraphs: [
        "Treatment-resistant depression (TRD) is major depressive disorder (MDD) that has not improved adequately after at least two antidepressant medication trials of adequate dose and duration in the current episode. This is the definition used in the FDA's 2018 draft guidance for developing drugs for treatment-resistant depression and in the esketamine (SPRAVATO®) clinical trials. Some definitions require trials of different drug classes, or a psychotherapy trial, before calling depression resistant.",
        "It is important to understand what TRD is not. It is not a separate diagnosis — it is a description of treatment history. It does not mean the depression is untreatable. It means the standard first steps (one or more antidepressant medications at adequate dose for adequate time) have not produced enough improvement, and it is time to consider other options.",
        "An adequate antidepressant trial generally means taking the medication at a therapeutic dose — not just a starting dose — consistently for about 6 to 8 weeks. A medication stopped early because of side effects, or never increased beyond the starting dose, is usually considered an incomplete or intolerable trial rather than a failed one. Reviewing pharmacy records and past prescriptions with a psychiatrist is the first step in deciding whether depression is truly resistant.",
      ],
    },
    {
      heading: "How Common Treatment-Resistant Depression Is: The STAR*D Data",
      paragraphs: [
        "The STAR*D study (Sequenced Treatment Alternatives to Relieve Depression) is the largest federally funded trial of antidepressant medications. It enrolled about 3,671 adults with major depressive disorder and tracked remission rates across up to four sequential medication trials. In STAR*D, remission rates were approximately 37% after the first medication, 31% after the second, 14% after the third, and 13% after the fourth (Rush et al., Am J Psychiatry 2006;163(11):1905–1917). The study was open-label without a placebo group, so exact figures are debated, but the pattern of diminishing returns is well established.",
        "What this means: roughly one-third of people treated for major depressive disorder do not reach remission after two treatment steps. By the time someone has tried multiple medications without success — the population SAINT® and other brain stimulation therapies are designed for — the odds of remission from the next medication alone are quite low. This is why TRD is common, and why a range of non-medication options exists.",
        "TRD is not rare. It affects millions of adults in the United States. The good news is that multiple evidence-based options are available, and many people with TRD do respond to a treatment they have not tried yet.",
      ],
    },
    {
      heading: "Why Depression Can Be Misdiagnosed as Treatment-Resistant",
      paragraphs: [
        "Before assuming depression is treatment-resistant, a careful reassessment is essential. Several common issues can mimic resistance:",
        "Bipolar disorder: Depressive episodes in bipolar disorder often respond poorly to antidepressants alone and can be mistaken for resistant unipolar depression. A missed bipolar diagnosis can lead to years of ineffective treatment. Screening for bipolar disorder — including asking about past periods of elevated mood, reduced need for sleep, or impulsive behavior — is essential.",
        "Inadequate trials: A medication taken at a sub-therapeutic dose, or stopped before 6 to 8 weeks, was not a true trial. Reviewing the actual dose and duration with a psychiatrist may reveal that past trials were inadequate rather than failed.",
        "Comorbid conditions: Untreated anxiety disorders, PTSD, ADHD, or substance use can interfere with depression treatment. Addressing these alongside the depression can change the picture.",
        "Medical conditions: Hypothyroidism, sleep apnea, vitamin deficiencies, and chronic pain can produce or worsen depressive symptoms. A medical workup including thyroid function is part of a thorough TRD evaluation.",
        "A careful reassessment sometimes changes the diagnosis or reveals a correctable cause before new treatments are tried. This is why the first step in TRD treatment is not another medication — it is a full re-evaluation.",
      ],
    },
    {
      heading:
        "Brain Stimulation: SAINT®, Standard TMS, Deep TMS, ECT, and VNS",
      paragraphs: [
        "Brain stimulation therapies use magnetic pulses, electrical currents, or implanted devices to directly modulate brain circuits involved in depression. They are an option when medication has not worked. The five major types differ in how they work, how long they take, their evidence, and their side-effect profiles.",
      ],
    },
    {
      heading:
        "SAINT®: Stanford Accelerated Intelligent Neuromodulation Therapy",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an accelerated, fMRI-guided TMS protocol. It uses the Magnus Neuromodulation System, which was FDA-cleared in September 2022 (510(k) K220177) for major depressive disorder in adults whose depression has not improved enough on prior antidepressant medication. It also received FDA Breakthrough Device designation.",
        "What makes SAINT® different from standard TMS is the schedule and the targeting. SAINT® delivers 10 sessions per day over 5 consecutive days (50 total sessions), using an intermittent theta-burst stimulation (iTBS) pulse pattern. Before treatment, a functional MRI (fMRI) scan identifies a personalized target in the dorsolateral prefrontal cortex (DLPFC) — the brain region stimulated during TMS — based on its anticorrelation with the subgenual anterior cingulate cortex (sgACC), a region often overactive in depression. The idea is to stimulate the exact spot in the DLPFC most likely to calm the overactive sgACC for each individual.",
        "In the 2022 double-blind randomized controlled trial, 79% of participants who received SAINT® reached remission after the five-day protocol, compared to 13% in the sham (placebo) control group (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Earlier open-label studies from the Stanford research group reported similar or higher remission rates, but open-label designs do not control for placebo effects the way a randomized trial does.",
        "SAINT® is FDA-cleared for MDD in adults. It is not cleared for anxiety, OCD, PTSD, bipolar disorder, or any other condition. NWCOA is a Verified SAINT® Provider in Coral Springs, Florida, and treats adults 22 and older. SAINT® at NWCOA starts at $25,000; coverage varies by plan and we verify your benefits for free. [[Learn more about SAINT®|/saint]] or [[see if you're a candidate|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Standard TMS (Repetitive TMS, rTMS)",
      paragraphs: [
        "Standard repetitive TMS (rTMS) delivers magnetic pulses through a coil placed on the scalp to stimulate the dorsolateral prefrontal cortex. It is FDA-cleared for adults with major depressive disorder who did not improve enough on prior antidepressant medication. A typical course is one session per day, 5 days per week, for about 6 weeks (approximately 30 sessions).",
        "In the pivotal randomized sham-controlled trial of 301 adults, active TMS was superior to sham. A naturalistic study of 307 patients reported about 58% response and 37% remission on clinician-rated scales. Results vary between individuals, the naturalistic study had no control group, and TMS does not help everyone.",
        "Standard TMS is non-invasive, does not require anesthesia, and is administered in an outpatient setting. Common side effects include headache, scalp discomfort, and facial twitching during treatment. The main drawback compared to SAINT® is time: 6 weeks of daily visits versus 5 days. [[Compare SAINT® vs standard TMS|/learn/saint-vs-accelerated-tms]].",
      ],
    },
    {
      heading: "Deep TMS (BrainsWay H-Coil)",
      paragraphs: [
        "Deep TMS uses a specialized H-coil (manufactured by BrainsWay) designed to stimulate deeper brain tissue than standard TMS coils. It is FDA-cleared for major depressive disorder in adults. The treatment schedule is similar to standard TMS — about 5 sessions per week for 6 weeks — and it is also non-invasive and outpatient.",
        "Published remission rates for deep TMS in depression range from about 30% to 35% across various studies. The deeper stimulation may reach different brain circuits than standard TMS, but the evidence does not show a clear superiority over standard TMS for depression. Side effects are similar (headache, scalp discomfort) with a small risk of seizure.",
        "Deep TMS is one option among several. Whether it is preferable to standard TMS or SAINT® depends on individual factors that should be discussed with a psychiatrist. [[Compare SAINT® vs deep TMS|/learn/saint-vs-deep-tms]].",
      ],
    },
    {
      heading: "ECT (Electroconvulsive Therapy)",
      paragraphs: [
        "Electroconvulsive therapy (ECT) is an established psychiatric treatment that uses brief electrical stimulation to the scalp to induce a controlled seizure while the patient is under general anesthesia. It has the highest remission rates of any depression treatment — roughly 50% to 70% for severe or psychotic depression — and is often used when depression is life-threatening, when rapid response is critical, or when other treatments have failed.",
        "ECT typically requires 6 to 12 sessions, given 2 to 3 times per week over 2 to 4 weeks, followed by maintenance sessions to prevent relapse. The main drawbacks are anesthesia risk and cognitive side effects — most commonly temporary memory difficulty for events around the treatment period, which usually improves over weeks to months.",
        "ECT remains a highly effective option for the right patient. It is not outdated; modern ECT uses refined electrical waveforms, careful anesthesia, and monitoring to minimize side effects. For severe, psychotic, or catatonic depression, it can be life-saving. [[Compare SAINT® vs ECT|/learn/saint-vs-ect]].",
      ],
    },
    {
      heading: "VNS (Vagus Nerve Stimulation)",
      paragraphs: [
        "Vagus nerve stimulation (VNS) involves surgically implanting a device that delivers regular electrical pulses to the vagus nerve in the neck. It is FDA-approved as an adjunctive (add-on) treatment for chronic or recurrent depression in adults who have not responded to at least four antidepressant trials. The device remains implanted and delivers stimulation continuously.",
        "VNS works gradually — improvement may take months to become apparent — and it is intended as a long-term, ongoing treatment rather than a quick fix. Because it requires surgery, it is typically considered after other options have not worked. Side effects include voice changes, throat discomfort, and cough, which usually adjust over time.",
        "VNS is a long-term option for a specific population: people with chronic, recurrent depression who have tried many treatments without success. [[Compare SAINT® vs VNS|/learn/saint-vs-vns]].",
      ],
    },
    {
      heading: "Rapid Relief: SPRAVATO® and Ketamine",
      paragraphs: [
        "Two treatments can produce mood changes faster than standard TMS or most medications: SPRAVATO® (esketamine) and IV ketamine. Both act on the glutamate system, which is different from how most antidepressants work (serotonin, norepinephrine, or dopamine). This may explain why they can help people who have not responded to standard antidepressants.",
      ],
    },
    {
      heading: "SPRAVATO® (Esketamine Nasal Spray)",
      paragraphs: [
        "SPRAVATO® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression in adults. It was first approved in 2019 to be taken with an oral antidepressant, and in January 2025 was approved as monotherapy (without an oral antidepressant). It is administered in a certified clinic under the SPRAVATO REMS (Risk Evaluation and Mitigation Strategy) program, which requires monitoring for at least 2 hours after each dose.",
        "A typical course starts with twice-weekly sessions for 4 weeks, then tapers to weekly, then every-other-week maintenance. In the pivotal trials, about 28% of participants reached remission. Individual results may vary. SPRAVATO® is not for everyone — it carries a boxed warning about sedation, dissociation, and potential for abuse, and it is contraindicated in certain medical conditions.",
        "SPRAVATO® may be covered by insurance; coverage varies by plan. NWCOA offers SPRAVATO® alongside SAINT® in the same clinic, so your psychiatrist can coordinate care. [[Compare SAINT® vs SPRAVATO®|/learn/saint-vs-spravato]].",
      ],
    },
    {
      heading: "IV Ketamine",
      paragraphs: [
        "Ketamine is an anesthetic that, at sub-anesthetic doses, can produce rapid antidepressant effects. IV ketamine for depression is used off-label — it is not FDA-approved for depression, though the intranasal esketamine form (SPRAVATO®) is. A typical course is 6 infusions over 2 to 3 weeks. Open-label studies have reported response rates of about 50% to 70%, but these studies lack placebo controls, and individual results may vary.",
        "The main limitation of ketamine is durability: mood improvements may fade within days to weeks after a course, and ongoing maintenance infusions are often needed. Side effects include dissociation, elevated blood pressure, and potential for abuse. IV ketamine is typically self-pay; coverage varies.",
        "Because ketamine and SAINT® work through different mechanisms, some people benefit from combining them. NWCOA offers both in the same clinic. [[Compare SAINT® vs ketamine|/learn/saint-vs-ketamine]].",
      ],
    },
    {
      heading: "Medication Strategies for Treatment-Resistant Depression",
      paragraphs: [
        "Medication remains a cornerstone of TRD treatment, even when prior medications have not worked. The STAR*D data show diminishing returns with each successive medication trial, but some people do respond to a new medication or combination. Strategies include:",
        "Optimization: Increasing the dose of a current antidepressant to a true therapeutic level, or extending the trial to a full 8 weeks before declaring it ineffective. Many apparent failures are actually inadequate trials.",
        "Switching: Changing to a medication with a different mechanism of action — for example, from an SSRI to an SNRI, or to a medication targeting different neurotransmitters. STAR*D found about 18% to 28% remission with a second or third medication.",
        "Augmentation: Adding a second medication to an existing antidepressant — such as adding an atypical antipsychotic (aripiprazole, quetiapine), a mood stabilizer (lithium), or a thyroid medication (triiodothyronine). Augmentation can boost response without discontinuing a partially helpful medication.",
        "The challenge with medication-only strategies is that each trial takes 6 to 8 weeks, and the odds of remission decline with each successive trial. For people who have already tried multiple medications, brain stimulation therapies or esketamine may offer a better chance of response in less time. [[Why isn't my antidepressant working?|/learn/why-isnt-my-antidepressant-working]] or [[alternatives to antidepressants|/learn/alternative-to-antidepressants]].",
      ],
    },
    {
      heading: "Psychotherapy for Treatment-Resistant Depression",
      paragraphs: [
        "Psychotherapy is an evidence-based treatment for depression, including treatment-resistant depression. The most studied forms include cognitive behavioral therapy (CBT), behavioral activation, and interpersonal therapy (IPT). In TRD populations, psychotherapy can be used alongside medication or brain stimulation, and it addresses the psychological and behavioral patterns that medication alone does not.",
        "CBT helps people identify and change negative thought patterns and behaviors that maintain depression. Behavioral activation focuses on re-engaging with rewarding activities. IPT addresses interpersonal problems that may be contributing to the depression. Response rates in TRD populations are roughly 30% to 40% in published studies, though these vary by population and design.",
        "Psychotherapy is not a substitute for brain stimulation or medication when those are needed, but it complements them. Skills learned in therapy can be durable — they do not wear off the way a medication's effects might after discontinuation. NWCOA offers psychotherapy alongside SAINT®, SPRAVATO®, and medication management in the same clinic.",
      ],
    },
    {
      heading: "How Long Do the Results Last? Durability Compared",
      paragraphs: [
        "Durability — how long the improvement lasts after a course of treatment ends — is one of the most important and least discussed factors in choosing a TRD treatment. Here is what the evidence shows for each option:",
        "SAINT®: The 2022 randomized trial measured outcomes at 5 days post-treatment. Longer follow-up data from the Stanford research group are emerging. Some participants maintained remission at 1 to 3 months, but relapse is possible, and the long-term durability data are still developing. Maintenance or repeat courses may be needed for some people. [[How long do SAINT® results last?|/learn/what-happens-after-saint-tms]].",
        "Standard TMS: Response can be sustained for months, and some people maintain improvement for a year or more. Maintenance TMS sessions (monthly or less frequent) may help sustain the benefit. Relapse rates without maintenance are estimated at 30% to 50% over 6 to 12 months in various studies.",
        "ECT: ECT has high initial response rates, but relapse without maintenance treatment is common — up to 50% or more within 6 months. Maintenance ECT (weekly to monthly sessions) or continuation medication can reduce relapse.",
        "SPRAVATO®: Maintenance sessions are built into the protocol. Without ongoing maintenance sessions, relapse rates are significant. The treatment is designed to be ongoing, not a one-time cure.",
        "IV ketamine: Mood improvements may fade within days to weeks after a course. Maintenance infusions (monthly or more frequent) are often needed to sustain response. The durability of ketamine is one of its main limitations compared to TMS.",
        "Medication: Antidepressants work only while taken. Discontinuation carries relapse risk, especially for people with recurrent depression. Long-term medication management is standard for TRD.",
        "Psychotherapy: Skills learned in therapy can be durable and do not require ongoing sessions to maintain, though periodic booster sessions can help.",
        "No treatment guarantees lasting remission. A realistic plan sets measurable goals, a reassessment date, and a plan for what to try next if the initial treatment does not hold. [[What if SAINT® doesn't work?|/learn/what-if-saint-doesnt-work]].",
      ],
    },
    {
      heading: "Cost and Insurance for TRD Treatments",
      paragraphs: [
        "Cost and insurance coverage vary widely by treatment, by plan, and by state. Here is what to expect for each major option:",
        "SAINT®: SAINT® at NWCOA starts at $25,000. Coverage varies by plan; some commercial plans and Medicare may cover SAINT® with prior authorization, but coverage is not universal. We verify your benefits for free before treatment begins, so you know your cost upfront. Financing is available on a case-by-case basis and discussed during consultation. [[SAINT® TMS cost|/learn/saint-tms-cost]].",
        "Standard TMS: May be covered by insurance for MDD that has not improved on prior antidepressant medication. Coverage varies by plan; prior authorization is typically required. Self-pay rates are lower than SAINT®.",
        "Deep TMS: May be covered; coverage varies by plan. Similar insurance pathway to standard TMS.",
        "ECT: Generally covered by insurance for severe depression. Requires anesthesia and a hospital or outpatient surgical setting.",
        "VNS: May be covered; coverage varies by plan. Requires surgery and ongoing follow-up. The device and procedure cost is significant.",
        "SPRAVATO®: May be covered by commercial and Medicare plans; coverage varies by plan. The REMS program requires in-clinic administration, which adds to the per-session cost.",
        "IV ketamine: Typically self-pay. Some plans may cover it for chronic pain but not for depression. Cost per infusion varies.",
        "Medication: Generally covered by insurance. Copays and formulary tiers vary.",
        "Psychotherapy: May be covered by insurance; coverage varies by plan. Session limits and copays apply.",
        "We verify your benefits for any carrier at no cost. A free consultation is the first step — we will review your coverage and out-of-pocket cost before you commit to any treatment. [[Check my insurance|/check-insurance-options]].",
      ],
    },
    {
      heading: "A Simple Decision Framework for TRD Treatment",
      paragraphs: [
        "Choosing a TRD treatment can feel overwhelming. Here is a step-by-step framework to help you and your psychiatrist decide:",
      ],
      list: [
        "Get a full re-evaluation: Review past medication trials (dose, duration, outcome), screen for bipolar disorder and comorbid conditions, and complete a medical workup including thyroid function.",
        "Confirm that past trials were adequate: Were they at a therapeutic dose for 6 to 8 weeks? If not, an optimized or extended trial may be worth trying before moving to brain stimulation.",
        "Consider time constraints: If you need rapid relief, SAINT® (5 days) or SPRAVATO® (hours to days) may be preferable to standard TMS (6 weeks) or medication (6 to 8 weeks per trial).",
        "Weigh invasiveness: SAINT®, standard TMS, and deep TMS are non-invasive and do not require anesthesia. ECT requires anesthesia. VNS requires surgery. SPRAVATO® and ketamine involve medication with dissociative effects.",
        "Review the evidence: Ask for the remission and response rates for the specific treatment and system being offered. The 79% remission figure applies to SAINT® specifically, not to other accelerated TMS protocols. [[SAINT® success rates|/learn/saint-tms-success-rates]].",
        "Check insurance and cost: Verify your benefits before committing. SAINT® at NWCOA starts at $25,000; coverage varies by plan. We verify benefits for free. [[Check my insurance|/check-insurance-options]].",
        "Plan for what comes after: Ask what follow-up is included, what the maintenance plan is, and what to try next if the treatment does not work. [[What if SAINT® doesn't work?|/learn/what-if-saint-doesnt-work]].",
        "Start with a free consultation: No treatment should begin without a thorough evaluation. A free consultation with our clinical team is the first step. [[Book a free consultation|/am-i-a-candidate-screener]] or call 954-827-9793.",
      ],
    },
    {
      heading: "Self-Check: Is It Time to Consider a Different Approach?",
      paragraphs: [
        "If you have been treated for depression with medication and have not improved as much as you hoped, you are not alone — and you have options. Treatment-resistant depression is common, and multiple evidence-based treatments exist that do not rely on another pill.",
        "The first step is a free consultation. We will review your treatment history, verify your insurance benefits at no cost, and help you understand which options may be right for your situation. There is no obligation, and no email is required to start the screener.",
        "[[Check if SAINT® is a fit — free screener|/am-i-a-candidate-screener]] · [[Verify my insurance benefits|/check-insurance-options]] · Call 954-827-9793",
        "If you are in crisis or having thoughts of self-harm, call or text 988 (Suicide & Crisis Lifeline) or go to the nearest emergency room. This page is for information, not emergency care.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2021;178(10):898-909.",
      url: "https://pubmed.ncbi.nlm.nih.gov/34256788/",
    },
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074943/",
    },
    {
      citation:
        "Trivedi MH, et al. Evaluation of outcomes with citalopram for depression using STAR*D. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "U.S. Food and Drug Administration. FDA Draft Guidance — Major Depressive Disorder: Developing Drugs for Treatment (2018).",
      url: "https://www.fda.gov/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. SPRAVATO (esketamine) approval — treatment-resistant depression (2019; monotherapy approval January 2025).",
      url: "https://www.fda.gov/",
    },
  ],
  relatedLinks: trdRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Quick Comparison Table",
      href: "#quick-comparison-every-trd-option-at-a-glance",
    },
    { label: "What TRD Is", href: "#what-treatment-resistant-depression-is" },
    {
      label: "How Common (STAR*D)",
      href: "#how-common-treatment-resistant-depression-is-the-stard-data",
    },
    {
      label: "Misdiagnosis",
      href: "#why-depression-can-be-misdiagnosed-as-treatment-resistant",
    },
    {
      label: "Brain Stimulation",
      href: "#brain-stimulation-saint-standard-tms-deep-tms-ect-and-vns",
    },
    {
      label: "SAINT®",
      href: "#saint-stanford-accelerated-intelligent-neuromodulation-therapy",
    },
    { label: "Standard TMS", href: "#standard-tms-repetitive-tms-rtms" },
    { label: "Deep TMS", href: "#deep-tms-brainsway-h-coil" },
    { label: "ECT", href: "#ect-electroconvulsive-therapy" },
    { label: "VNS", href: "#vns-vagus-nerve-stimulation" },
    {
      label: "SPRAVATO® & Ketamine",
      href: "#rapid-relief-spravato-and-ketamine",
    },
    {
      label: "Medication Strategies",
      href: "#medication-strategies-for-treatment-resistant-depression",
    },
    {
      label: "Psychotherapy",
      href: "#psychotherapy-for-treatment-resistant-depression",
    },
    {
      label: "Durability Compared",
      href: "#how-long-do-the-results-last-durability-compared",
    },
    {
      label: "Cost & Insurance",
      href: "#cost-and-insurance-for-trd-treatments",
    },
    {
      label: "Decision Framework",
      href: "#a-simple-decision-framework-for-trd-treatment",
    },
    {
      label: "Self-Check",
      href: "#self-check-is-it-time-to-consider-a-different-approach",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
